import type { ComponentDefinition } from "../types";

export const ENGINE: ComponentDefinition[] = [
  {
    id: "src-engine",
    name: "Generic Engine",
    shortName: "Generic Engine",
    subtitle: "Configurable starter + alternator",
    category: "engine",
    ampDraw: 180,
    ampSurge: 400,
    ampOutput: 100,
    width: 220,
    height: 130,
    terminals: [
      {
        id: "starter",
        label: "Starter",
        polarity: "positive",
        direction: "input",
        side: "right",
        offset: 0.25,
        maxAmp: 600,
      },
      {
        id: "alt-pos",
        label: "Alt B+",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.5,
        maxAmp: 200,
      },
      {
        id: "gnd",
        label: "Block GND",
        polarity: "ground",
        direction: "bidi",
        side: "right",
        offset: 0.75,
        maxAmp: 600,
      },
    ],
    simulateStates: {
      default: "off",
      options: [
        {
          id: "starting",
          label: "Starting",
          role: "draw",
        },
        {
          id: "running",
          label: "Running",
          role: "output",
        },
        {
          id: "off",
          label: "Off",
          ampOverride: 0,
        },
      ],
    },
  },
  {
    id: "src-engine-15hp",
    name: "Diesel Engine — 15 HP",
    shortName: "Engine 15HP",
    subtitle: "1-cyl ~15 HP (Yanmar 1GM10 class)",
    category: "engine",
    ampDraw: 100,
    ampSurge: 200,
    ampOutput: 50,
    width: 220,
    height: 130,
    terminals: [
      {
        id: "starter",
        label: "Starter",
        polarity: "positive",
        direction: "input",
        side: "right",
        offset: 0.25,
        maxAmp: 300,
      },
      {
        id: "alt-pos",
        label: "Alt B+",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.5,
        maxAmp: 200,
      },
      {
        id: "gnd",
        label: "Block GND",
        polarity: "ground",
        direction: "bidi",
        side: "right",
        offset: 0.75,
        maxAmp: 300,
      },
    ],
    simulateStates: {
      default: "off",
      options: [
        {
          id: "starting",
          label: "Starting",
          role: "draw",
        },
        {
          id: "running",
          label: "Running",
          role: "output",
        },
        {
          id: "off",
          label: "Off",
          ampOverride: 0,
        },
      ],
    },
  },
  {
    id: "src-engine-30hp",
    name: "Diesel Engine — 30 HP",
    shortName: "Engine 30HP",
    subtitle: "3-cyl ~30 HP (Yanmar 3GM30 class)",
    category: "engine",
    ampDraw: 150,
    ampSurge: 300,
    ampOutput: 80,
    width: 220,
    height: 130,
    terminals: [
      {
        id: "starter",
        label: "Starter",
        polarity: "positive",
        direction: "input",
        side: "right",
        offset: 0.25,
        maxAmp: 500,
      },
      {
        id: "alt-pos",
        label: "Alt B+",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.5,
        maxAmp: 200,
      },
      {
        id: "gnd",
        label: "Block GND",
        polarity: "ground",
        direction: "bidi",
        side: "right",
        offset: 0.75,
        maxAmp: 500,
      },
    ],
    simulateStates: {
      default: "off",
      options: [
        {
          id: "starting",
          label: "Starting",
          role: "draw",
        },
        {
          id: "running",
          label: "Running",
          role: "output",
        },
        {
          id: "off",
          label: "Off",
          ampOverride: 0,
        },
      ],
    },
  },
  {
    id: "src-engine-75hp",
    name: "Diesel Engine — 75 HP",
    shortName: "Engine 75HP",
    subtitle: "4-cyl ~75 HP (Volvo D2-75 / Yanmar 4JH class)",
    category: "engine",
    ampDraw: 250,
    ampSurge: 500,
    ampOutput: 115,
    width: 220,
    height: 130,
    terminals: [
      {
        id: "starter",
        label: "Starter",
        polarity: "positive",
        direction: "input",
        side: "right",
        offset: 0.25,
        maxAmp: 700,
      },
      {
        id: "alt-pos",
        label: "Alt B+",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.5,
        maxAmp: 200,
      },
      {
        id: "gnd",
        label: "Block GND",
        polarity: "ground",
        direction: "bidi",
        side: "right",
        offset: 0.75,
        maxAmp: 700,
      },
    ],
    simulateStates: {
      default: "off",
      options: [
        {
          id: "starting",
          label: "Starting",
          role: "draw",
        },
        {
          id: "running",
          label: "Running",
          role: "output",
        },
        {
          id: "off",
          label: "Off",
          ampOverride: 0,
        },
      ],
    },
  },
  {
    id: "src-engine-yanmar-3ym30",
    name: "Engine 29HP — Yanmar 3YM30",
    shortName: "3YM30 29HP",
    subtitle: "Yanmar 3YM30 (3-cyl sailboat diesel)",
    category: "engine",
    manufacturer: "Yanmar",
    model: "3YM30",
    // Cranking figures unpublished by Yanmar; ampDraw/ampSurge below are modeled.
    ampDraw: 250,
    ampSurge: 350,
    ampOutput: 60,
    width: 220,
    height: 130,
    terminals: [
      {
        id: "starter",
        label: "Starter",
        polarity: "positive",
        direction: "input",
        side: "right",
        offset: 0.25,
        maxAmp: 500,
      },
      {
        id: "alt-pos",
        label: "Alt B+",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.5,
        maxAmp: 200,
      },
      {
        id: "gnd",
        label: "Block GND",
        polarity: "ground",
        direction: "bidi",
        side: "right",
        offset: 0.75,
        maxAmp: 500,
      },
    ],
    simulateStates: {
      default: "off",
      options: [
        {
          id: "starting",
          label: "Starting",
          role: "draw",
        },
        {
          id: "running",
          label: "Running",
          role: "output",
        },
        {
          id: "off",
          label: "Off",
          ampOverride: 0,
        },
      ],
    },
    image: "src-engine-yanmar-3ym30.webp",
  },
  {
    id: "src-engine-yanmar-1gm10",
    name: "Engine 9HP — Yanmar 1GM10",
    shortName: "1GM10 9HP",
    subtitle: "Yanmar 1GM10 (1-cyl sailboat diesel)",
    category: "engine",
    manufacturer: "Yanmar",
    model: "1GM10",
    // Cranking figures unpublished by Yanmar; ampDraw/ampSurge below are modeled.
    ampDraw: 150,
    ampSurge: 250,
    ampOutput: 40,
    width: 220,
    height: 130,
    terminals: [
      {
        id: "starter",
        label: "Starter",
        polarity: "positive",
        direction: "input",
        side: "right",
        offset: 0.25,
        maxAmp: 250,
      },
      {
        id: "alt-pos",
        label: "Alt B+",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.5,
        maxAmp: 200,
      },
      {
        id: "gnd",
        label: "Block GND",
        polarity: "ground",
        direction: "bidi",
        side: "right",
        offset: 0.75,
        maxAmp: 250,
      },
    ],
    simulateStates: {
      default: "off",
      options: [
        {
          id: "starting",
          label: "Starting",
          role: "draw",
        },
        {
          id: "running",
          label: "Running",
          role: "output",
        },
        {
          id: "off",
          label: "Off",
          ampOverride: 0,
        },
      ],
    },
    image: "src-engine-yanmar-1gm10.webp",
  },
  {
    id: "src-engine-yanmar-4jh2e",
    name: "Engine 50HP — Yanmar 4JH2E",
    shortName: "4JH2E 50HP",
    subtitle: "Yanmar 4JH2E (4-cyl sailboat diesel)",
    category: "engine",
    manufacturer: "Yanmar",
    model: "4JH2E",
    ampDraw: 180,
    ampSurge: 360,
    ampOutput: 55,
    width: 220,
    height: 130,
    terminals: [
      {
        id: "starter",
        label: "Starter",
        polarity: "positive",
        direction: "input",
        side: "right",
        offset: 0.25,
        maxAmp: 600,
      },
      {
        id: "alt-pos",
        label: "Alt B+",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.5,
        maxAmp: 200,
      },
      {
        id: "gnd",
        label: "Block GND",
        polarity: "ground",
        direction: "bidi",
        side: "right",
        offset: 0.75,
        maxAmp: 600,
      },
    ],
    simulateStates: {
      default: "off",
      options: [
        {
          id: "starting",
          label: "Starting",
          role: "draw",
        },
        {
          id: "running",
          label: "Running",
          role: "output",
        },
        {
          id: "off",
          label: "Off",
          ampOverride: 0,
        },
      ],
    },
    image: "src-engine-yanmar-4jh2e.webp",
  },
  {
    id: "src-engine-volvo-d2-40",
    name: "Engine 38HP — Volvo Penta D2-40",
    shortName: "D2-40 38HP",
    subtitle: "Volvo Penta D2-40 (4-cyl sailboat diesel)",
    category: "engine",
    manufacturer: "Volvo Penta",
    model: "D2-40",
    ampDraw: 180,
    ampSurge: 360,
    ampOutput: 115,
    width: 220,
    height: 130,
    terminals: [
      {
        id: "starter",
        label: "Starter",
        polarity: "positive",
        direction: "input",
        side: "right",
        offset: 0.25,
        maxAmp: 600,
      },
      {
        id: "alt-pos",
        label: "Alt B+",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.5,
        maxAmp: 200,
      },
      {
        id: "gnd",
        label: "Block GND",
        polarity: "ground",
        direction: "bidi",
        side: "right",
        offset: 0.75,
        maxAmp: 600,
      },
    ],
    simulateStates: {
      default: "off",
      options: [
        {
          id: "starting",
          label: "Starting",
          role: "draw",
        },
        {
          id: "running",
          label: "Running",
          role: "output",
        },
        {
          id: "off",
          label: "Off",
          ampOverride: 0,
        },
      ],
    },
    image: "src-engine-volvo-d2-40.webp",
  },
  {
    id: "src-engine-volvo-d2-60",
    name: "Engine 60HP — Volvo Penta D2-60",
    shortName: "D2-60 60HP",
    subtitle: "Volvo Penta D2-60 (4-cyl sailboat diesel)",
    category: "engine",
    manufacturer: "Volvo Penta",
    model: "D2-60",
    ampDraw: 220,
    ampSurge: 440,
    ampOutput: 115,
    width: 220,
    height: 130,
    terminals: [
      {
        id: "starter",
        label: "Starter",
        polarity: "positive",
        direction: "input",
        side: "right",
        offset: 0.25,
        maxAmp: 600,
      },
      {
        id: "alt-pos",
        label: "Alt B+",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.5,
        maxAmp: 200,
      },
      {
        id: "gnd",
        label: "Block GND",
        polarity: "ground",
        direction: "bidi",
        side: "right",
        offset: 0.75,
        maxAmp: 600,
      },
    ],
    simulateStates: {
      default: "off",
      options: [
        {
          id: "starting",
          label: "Starting",
          role: "draw",
        },
        {
          id: "running",
          label: "Running",
          role: "output",
        },
        {
          id: "off",
          label: "Off",
          ampOverride: 0,
        },
      ],
    },
    image: "src-engine-volvo-d2-60.webp",
  },
  {
    id: "src-engine-beta-30",
    name: "Engine — Beta Marine 30",
    shortName: "Beta 30",
    subtitle: "Beta Marine 30 (3-cyl Kubota-based)",
    category: "engine",
    manufacturer: "Beta Marine",
    model: "Beta 30",
    ampDraw: 140,
    ampSurge: 280,
    ampOutput: 70,
    width: 220,
    height: 130,
    terminals: [
      {
        id: "starter",
        label: "Starter",
        polarity: "positive",
        direction: "input",
        side: "right",
        offset: 0.25,
        maxAmp: 500,
      },
      {
        id: "alt-pos",
        label: "Alt B+",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.5,
        maxAmp: 200,
      },
      {
        id: "gnd",
        label: "Block GND",
        polarity: "ground",
        direction: "bidi",
        side: "right",
        offset: 0.75,
        maxAmp: 500,
      },
    ],
    simulateStates: {
      default: "off",
      options: [
        {
          id: "starting",
          label: "Starting",
          role: "draw",
        },
        {
          id: "running",
          label: "Running",
          role: "output",
        },
        {
          id: "off",
          label: "Off",
          ampOverride: 0,
        },
      ],
    },
    image: "src-engine-beta-30.webp",
  },
  {
    id: "src-engine-westerbeke-30b",
    name: "Engine 27HP — Westerbeke 30B Three",
    shortName: "30B Three 27HP",
    subtitle: "Westerbeke 30B Three (3-cyl sailboat diesel)",
    category: "engine",
    manufacturer: "Westerbeke",
    model: "30B Three",
    ampDraw: 190,
    ampSurge: 280,
    ampOutput: 50,
    width: 220,
    height: 130,
    terminals: [
      {
        id: "starter",
        label: "Starter",
        polarity: "positive",
        direction: "input",
        side: "right",
        offset: 0.25,
        maxAmp: 500,
      },
      {
        id: "alt-pos",
        label: "Alt B+",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.5,
        maxAmp: 200,
      },
      {
        id: "gnd",
        label: "Block GND",
        polarity: "ground",
        direction: "bidi",
        side: "right",
        offset: 0.75,
        maxAmp: 500,
      },
    ],
    simulateStates: {
      default: "off",
      options: [
        {
          id: "starting",
          label: "Starting",
          role: "draw",
        },
        {
          id: "running",
          label: "Running",
          role: "output",
        },
        {
          id: "off",
          label: "Off",
          ampOverride: 0,
        },
      ],
    },
    image: "src-engine-westerbeke-30b.webp",
  },
  {
    id: "src-engine-volvo-md7a",
    name: "Engine 13HP — Volvo Penta MD7A",
    shortName: "MD7A 13HP",
    subtitle: "Volvo Penta MD7A (2-cyl, early 1980s)",
    category: "engine",
    manufacturer: "Volvo Penta",
    model: "MD7A",
    ampDraw: 95,
    ampSurge: 190,
    ampOutput: 35,
    width: 220,
    height: 130,
    terminals: [
      {
        id: "starter",
        label: "Starter",
        polarity: "positive",
        direction: "input",
        side: "right",
        offset: 0.25,
        maxAmp: 300,
      },
      {
        id: "alt-pos",
        label: "Alt B+",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.5,
        maxAmp: 200,
      },
      {
        id: "gnd",
        label: "Block GND",
        polarity: "ground",
        direction: "bidi",
        side: "right",
        offset: 0.75,
        maxAmp: 300,
      },
    ],
    simulateStates: {
      default: "off",
      options: [
        {
          id: "starting",
          label: "Starting",
          role: "draw",
        },
        {
          id: "running",
          label: "Running",
          role: "output",
        },
        {
          id: "off",
          label: "Off",
          ampOverride: 0,
        },
      ],
    },
  },
  {
    id: "src-engine-volvo-md5",
    name: "Engine 7.5HP — Volvo Penta MD5",
    shortName: "MD5 7.5HP",
    subtitle: "Volvo Penta MD5 (1-cyl, mid 70s–early 80s)",
    category: "engine",
    manufacturer: "Volvo Penta",
    model: "MD5",
    ampDraw: 80,
    ampSurge: 160,
    ampOutput: 35,
    width: 220,
    height: 130,
    terminals: [
      {
        id: "starter",
        label: "Starter",
        polarity: "positive",
        direction: "input",
        side: "right",
        offset: 0.25,
        maxAmp: 250,
      },
      {
        id: "alt-pos",
        label: "Alt B+",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.5,
        maxAmp: 200,
      },
      {
        id: "gnd",
        label: "Block GND",
        polarity: "ground",
        direction: "bidi",
        side: "right",
        offset: 0.75,
        maxAmp: 250,
      },
    ],
    simulateStates: {
      default: "off",
      options: [
        {
          id: "starting",
          label: "Starting",
          role: "draw",
        },
        {
          id: "running",
          label: "Running",
          role: "output",
        },
        {
          id: "off",
          label: "Off",
          ampOverride: 0,
        },
      ],
    },
  },
  {
    id: "src-engine-volvo-md2020",
    name: "Engine 19HP — Volvo Penta MD2020",
    shortName: "MD2020 19HP",
    subtitle: "Volvo Penta MD2020 (3-cyl, 1996–2006)",
    category: "engine",
    manufacturer: "Volvo Penta",
    model: "MD2020",
    ampDraw: 110,
    ampSurge: 220,
    ampOutput: 60,
    width: 220,
    height: 130,
    terminals: [
      {
        id: "starter",
        label: "Starter",
        polarity: "positive",
        direction: "input",
        side: "right",
        offset: 0.25,
        maxAmp: 400,
      },
      {
        id: "alt-pos",
        label: "Alt B+",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.5,
        maxAmp: 200,
      },
      {
        id: "gnd",
        label: "Block GND",
        polarity: "ground",
        direction: "bidi",
        side: "right",
        offset: 0.75,
        maxAmp: 400,
      },
    ],
    simulateStates: {
      default: "off",
      options: [
        {
          id: "starting",
          label: "Starting",
          role: "draw",
        },
        {
          id: "running",
          label: "Running",
          role: "output",
        },
        {
          id: "off",
          label: "Off",
          ampOverride: 0,
        },
      ],
    },
    image: "src-engine-volvo-md2020.webp",
  },
  {
    id: "src-engine-bukh-dv20",
    name: "Engine 20HP — Bukh DV20",
    shortName: "DV20 20HP",
    subtitle: "Bukh DV20 (2-cyl, Danish-built)",
    category: "engine",
    manufacturer: "Bukh",
    model: "DV20",
    ampDraw: 110,
    ampSurge: 220,
    ampOutput: 55,
    width: 220,
    height: 130,
    terminals: [
      {
        id: "starter",
        label: "Starter",
        polarity: "positive",
        direction: "input",
        side: "right",
        offset: 0.25,
        maxAmp: 400,
      },
      {
        id: "alt-pos",
        label: "Alt B+",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.5,
        maxAmp: 200,
      },
      {
        id: "gnd",
        label: "Block GND",
        polarity: "ground",
        direction: "bidi",
        side: "right",
        offset: 0.75,
        maxAmp: 400,
      },
    ],
    simulateStates: {
      default: "off",
      options: [
        {
          id: "starting",
          label: "Starting",
          role: "draw",
        },
        {
          id: "running",
          label: "Running",
          role: "output",
        },
        {
          id: "off",
          label: "Off",
          ampOverride: 0,
        },
      ],
    },
  },
  {
    id: "src-engine-volvo-md11c",
    name: "Engine 23HP — Volvo Penta MD11C",
    shortName: "MD11C 23HP",
    subtitle: "Volvo Penta MD11C (2-cyl, late 70s–mid 80s)",
    category: "engine",
    manufacturer: "Volvo Penta",
    model: "MD11C",
    ampDraw: 110,
    ampSurge: 220,
    ampOutput: 35,
    width: 220,
    height: 130,
    terminals: [
      {
        id: "starter",
        label: "Starter",
        polarity: "positive",
        direction: "input",
        side: "right",
        offset: 0.25,
        maxAmp: 400,
      },
      {
        id: "alt-pos",
        label: "Alt B+",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.5,
        maxAmp: 200,
      },
      {
        id: "gnd",
        label: "Block GND",
        polarity: "ground",
        direction: "bidi",
        side: "right",
        offset: 0.75,
        maxAmp: 400,
      },
    ],
    simulateStates: {
      default: "off",
      options: [
        {
          id: "starting",
          label: "Starting",
          role: "draw",
        },
        {
          id: "running",
          label: "Running",
          role: "output",
        },
        {
          id: "off",
          label: "Off",
          ampOverride: 0,
        },
      ],
    },
  },
  {
    id: "src-engine-volvo-md2030",
    name: "Engine 28HP — Volvo Penta MD2030",
    shortName: "MD2030 28HP",
    subtitle: "Volvo Penta MD2030 (3-cyl, 1996–2006)",
    category: "engine",
    manufacturer: "Volvo Penta",
    model: "MD2030",
    ampDraw: 150,
    ampSurge: 300,
    ampOutput: 60,
    width: 220,
    height: 130,
    terminals: [
      {
        id: "starter",
        label: "Starter",
        polarity: "positive",
        direction: "input",
        side: "right",
        offset: 0.25,
        maxAmp: 500,
      },
      {
        id: "alt-pos",
        label: "Alt B+",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.5,
        maxAmp: 200,
      },
      {
        id: "gnd",
        label: "Block GND",
        polarity: "ground",
        direction: "bidi",
        side: "right",
        offset: 0.75,
        maxAmp: 500,
      },
    ],
    simulateStates: {
      default: "off",
      options: [
        {
          id: "starting",
          label: "Starting",
          role: "draw",
        },
        {
          id: "running",
          label: "Running",
          role: "output",
        },
        {
          id: "off",
          label: "Off",
          ampOverride: 0,
        },
      ],
    },
    image: "src-engine-volvo-md2030.webp",
  },
  {
    id: "src-engine-volvo-md17c",
    name: "Engine 36HP — Volvo Penta MD17C",
    shortName: "MD17C 36HP",
    subtitle: "Volvo Penta MD17C (3-cyl, late 70s–mid 80s)",
    category: "engine",
    manufacturer: "Volvo Penta",
    model: "MD17C",
    // Volvo operator manual 3167, p28: original 35 A alternator; verify refits.
    ampDraw: 150,
    ampSurge: 300,
    ampOutput: 35,
    width: 220,
    height: 130,
    terminals: [
      {
        id: "starter",
        label: "Starter",
        polarity: "positive",
        direction: "input",
        side: "right",
        offset: 0.25,
        maxAmp: 500,
      },
      {
        id: "alt-pos",
        label: "Alt B+",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.5,
        maxAmp: 200,
      },
      {
        id: "gnd",
        label: "Block GND",
        polarity: "ground",
        direction: "bidi",
        side: "right",
        offset: 0.75,
        maxAmp: 500,
      },
    ],
    simulateStates: {
      default: "off",
      options: [
        {
          id: "starting",
          label: "Starting",
          role: "draw",
        },
        {
          id: "running",
          label: "Running",
          role: "output",
        },
        {
          id: "off",
          label: "Off",
          ampOverride: 0,
        },
      ],
    },
  },
];
